import { useRef, useState } from "react";
import type { Category, Member, ResultSet, Suggestion } from "../api/types";
import { VENUE_ATTRIBUTES, activeAttrs } from "../utils/attributes";
import { EventsSection } from "./EventsSection";
import { RatingCard } from "./RatingCard";
import { MeetingMap } from "./MeetingMap";
import {
  canNativeShare,
  emailUrl,
  formatShareText,
  nativeShare,
  whatsappUrl,
} from "../utils/share";

const CATEGORY_LABELS: Record<Category, string> = {
  food: "Food",
  drinks: "Drinks",
  activity: "Activities",
  sport: "Sport & Fitness",
  culture: "Culture",
  outdoors: "Outdoors",
  work: "Work & Meet",
};

/** Stable display order for category groups. */
const CATEGORY_ORDER: Category[] = [
  "food", "drinks", "activity", "sport", "culture", "outdoors", "work",
];

interface Props {
  results: ResultSet;
  members: Member[];
  apiKey: string | null;
  timeRequested: boolean;
  onFeedback: (venueId: string, workedOut: boolean) => void;
}

function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard) return navigator.clipboard.writeText(text);
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
  return Promise.resolve();
}

/** Group suggestions by their primary category and also categories. */
function groupByCategory(
  suggestions: Suggestion[],
): { category: Category; label: string; items: Suggestion[] }[] {
  const map = new Map<Category, Suggestion[]>();
  const seen = new Map<Category, Set<string>>();
  for (const s of suggestions) {
    const kind = s.venue.kind as Category;
    if (!map.has(kind)) { map.set(kind, []); seen.set(kind, new Set()); }
    map.get(kind)!.push(s);
    seen.get(kind)!.add(s.venue.venue_id);
    // Also add to secondary category groups
    for (const also of s.venue.also ?? []) {
      const cat = also as Category;
      if (cat !== kind) {
        if (!map.has(cat)) { map.set(cat, []); seen.set(cat, new Set()); }
        if (!seen.get(cat)!.has(s.venue.venue_id)) {
          map.get(cat)!.push(s);
          seen.get(cat)!.add(s.venue.venue_id);
        }
      }
    }
  }
  return CATEGORY_ORDER
    .filter((cat) => map.has(cat))
    .map((cat) => ({
      category: cat,
      label: CATEGORY_LABELS[cat] ?? cat,
      items: map.get(cat)!,
    }));
}

export function Results({ results, members, apiKey, timeRequested, onFeedback }: Props) {
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  function handleCopy() {
    copyToClipboard(formatShareText(results, members)).then(() => {
      setCopied(true);
      setMenuOpen(false);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleNativeShare() {
    nativeShare(results, members).catch(() => {});
    setMenuOpen(false);
  }

  const groups = groupByCategory(results.results);

  return (
    <section className="card">
      <header className="headline">
        <div className="headline-row">
          <h2>
            {results.zone.compromise_zone
              ? "No spot works for everyone"
              : `Nobody drives more than ${Math.round(results.max_travel_minutes)} min`}
          </h2>
          <div className="share-wrap" ref={menuRef}>
            <button
              type="button"
              className={`share-btn${copied ? " share-copied" : ""}`}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {copied ? "Copied!" : "Share"}
            </button>
            {menuOpen && (
              <div className="share-menu">
                <a
                  href={whatsappUrl(results, members)}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="share-option"
                  onClick={() => setMenuOpen(false)}
                >
                  WhatsApp
                </a>
                <a
                  href={emailUrl(results, members)}
                  className="share-option"
                  onClick={() => setMenuOpen(false)}
                >
                  Email
                </a>
                <button
                  type="button"
                  className="share-option"
                  onClick={handleCopy}
                >
                  Copy text
                </button>
                {canNativeShare() && (
                  <button
                    type="button"
                    className="share-option"
                    onClick={handleNativeShare}
                  >
                    More...
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        {results.zone.compromise_zone && (
          <p className="warn small">
            Too far apart for a genuinely fair point. These are the best
            compromises — check the per-person times.
          </p>
        )}
      </header>

      {apiKey && results.results.length > 0 && (
        <MeetingMap
          zone={results.zone}
          venues={results.results}
          members={members}
          apiKey={apiKey}
        />
      )}

      {results.notes.length > 0 && (
        <div className="notes">
          {results.notes.map((note) => (
            <p key={note} className="muted small">{note}</p>
          ))}
        </div>
      )}

      <div className="result-groups">
        {groups.map((group) => (
          <CategoryGroup
            key={group.category}
            category={group.category}
            label={group.label}
            items={group.items}
            apiKey={apiKey}
            timeRequested={timeRequested}
            onFeedback={onFeedback}
          />
        ))}
      </div>

      <EventsSection events={results.events ?? []} />
    </section>
  );
}

function CategoryGroup({
  category,
  label,
  items,
  apiKey,
  timeRequested,
  onFeedback,
}: {
  category: Category;
  label: string;
  items: Suggestion[];
  apiKey: string | null;
  timeRequested: boolean;
  onFeedback: (venueId: string, workedOut: boolean) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className={`result-group result-group--${category}`}>
      <button
        type="button"
        className="result-group-head"
        onClick={() => setCollapsed(!collapsed)}
        aria-expanded={!collapsed}
      >
        <span className="result-group-label">{label}</span>
        <span className="result-group-count">{items.length}</span>
        <span className={`result-group-arrow${collapsed ? " collapsed" : ""}`} />
      </button>
      {!collapsed && (
        <ol className="results">
          {items.map((suggestion) => (
            <ResultRow
              key={suggestion.venue.venue_id}
              suggestion={suggestion}
              apiKey={apiKey}
              timeRequested={timeRequested}
              onFeedback={onFeedback}
            />
          ))}
        </ol>
      )}
    </div>
  );
}

function ResultRow({
  suggestion,
  apiKey,
  timeRequested,
  onFeedback,
}: {
  suggestion: Suggestion;
  apiKey: string | null;
  timeRequested: boolean;
  onFeedback: (venueId: string, workedOut: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState<boolean | null>(null);
  const { venue } = suggestion;

  return (
    <li className="result">
      <button
        type="button"
        className="result-head"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <div>
          <span className="venue-name">{venue.name}</span>
          {venue.subcategory && (
            <span className="kind">{venue.subcategory}</span>
          )}
          {venue.also && venue.also.map((cat) => (
            <span key={cat} className="kind kind-also">
              {CATEGORY_LABELS[cat] ?? cat}
            </span>
          ))}
        </div>
        <span className="travel">{Math.round(suggestion.max_travel_minutes)} min</span>
      </button>

      {/* Rating + description */}
      {(venue.rating || venue.description) && (
        <div className="venue-meta">
          {venue.rating != null && (
            <span className="venue-rating">
              {"★"} {venue.rating.toFixed(1)}
              {venue.rating_count != null && <span className="muted"> ({venue.rating_count})</span>}
            </span>
          )}
          {venue.description && <span className="venue-desc muted">{venue.description}</span>}
        </div>
      )}

      {/* Per-member summary + key venue info */}
      <div className="member-summary">
        {suggestion.per_member.map((m) => (
          <span key={m.member_id} className="member-pill">
            {m.name}: {Math.round(m.travel_minutes)} min
            {m.distance_km != null && ` / ${m.distance_km} km`}
          </span>
        ))}
        {venue.price_band && (
          <span className="member-pill price-pill">
            {venue.price_band === "low" ? "$" : venue.price_band === "mid" ? "$$" : "$$$"}
          </span>
        )}
        {venue.attrs.good_for_groups === "yes" && (
          <span className="member-pill group-pill">Good for groups</span>
        )}
      </div>

      {open && (
        <div className="detail">
          {(() => {
            const keys = activeAttrs(venue.attrs);
            if (keys.length === 0) return null;
            const labelMap = Object.fromEntries(
              VENUE_ATTRIBUTES.map((a) => [a.key, a.label]),
            );
            return (
              <div className="attr-tags">
                {keys.map((key) => (
                  <span key={key} className="attr-tag">
                    {labelMap[key] ?? key}
                  </span>
                ))}
              </div>
            );
          })()}

          {venue.address && <p className="muted small">{venue.address}</p>}
          <p className="small">
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${venue.point.lat},${venue.point.lng}${venue.google_place_id ? `&destination_place_id=${venue.google_place_id}` : ""}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              Get directions
            </a>
          </p>
          {timeRequested && venue.open_during_window !== true && (
            <p className="muted small">Hours not confirmed — worth checking.</p>
          )}
          {venue.website && (
            <p className="small">
              <a href={venue.website} target="_blank" rel="noreferrer noopener">
                Visit website
              </a>
            </p>
          )}

          <RatingCard placeId={venue.google_place_id} apiKey={apiKey} />

          <div className="feedback">
            {feedbackSent === null ? (
              <>
                <span className="muted small">Been here?</span>
                <button type="button" className="link" onClick={() => { onFeedback(venue.venue_id, true); setFeedbackSent(true); }}>
                  Worked well
                </button>
                <button type="button" className="link" onClick={() => { onFeedback(venue.venue_id, false); setFeedbackSent(false); }}>
                  Not great
                </button>
              </>
            ) : (
              <span className="muted small">Thanks for the feedback!</span>
            )}
          </div>
        </div>
      )}
    </li>
  );
}
